/*------------------------------------------------------------------------------------------
* © 2020 KPMG LLP, a Delaware limited liability partnership and 
* a member firm of the KPMG global organization of independent member firms 
* affiliated with KPMG International Limited, a private English company limited by guarantee. 
* All rights reserved.
*-----------------------------------------------------------------------------------------*/
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { User, ProvisionUser, UserWithRole, Role } from 'src/app/models/user.model';
import { EnvSettingsService } from 'src/app/core/services/env-settings.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DefaultIntangibleMapping, MappingGroup, ProjectIntangibleMapping, KeyWord, KeyWordGroupMapper, MappingGroupWithKeyWord, BalanceSheetCategory, IncomeStatementCategory,  IncomeTaxDisclosureCategory, DefaultMappingGroup, TaxTagCategory, ApplicationInfo, NetworkRoleDescription, IndustrySets, ReportIdsForIndustrySet } from 'src/app/models/admin-mapping.model';
import { ExtractionDetail, ExtractionQuery } from 'src/app/models/extractor.model';
import { Company, CompanyFiling } from 'src/app/models/client.model';
import { CompanyAcquisitions} from 'src/app/models/project.model';
import { CIQInfo, CIQMapID, CIQMapCount } from 'src/app/models/admin-ciq-mapping.model';
import { CIQExtraction } from 'src/app/models/extractor.model';
import { UserRolesElement } from 'src/app/models/user-element-role.model';

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  private _adminEVSMappingApiRoute = '/api/V1/EVSAdminMapping';
  private _adminTaxMappingApiRoute = '/api/V1/TaxAdminMapping';
  private _adminApiRoute = '/api/V1/Admin';
  private _commonApiRoute = '/api/V1/Common';
  private _industryApiRoute='/api/V1/Industry';
  private _taxExtractionApiRoute='/api/V1/TaxExtraction';
  private _extractionApiRoute='/api/V1/Extraction';
  private _evsExtractionApiRoute='/api/V1/EvsExtraction';
  private _ciqMappingApiRoute='/api/V1/CapIQMapping';
  private _ciqExtractionApiRoute='/api/V1/CapIQExtraction';

  constructor(private _envSettings: EnvSettingsService, private _http: HttpClient) {

  }
  get adminEVSMappingServiceUrl() {
    return this._envSettings.getApiUrl() + this._adminEVSMappingApiRoute;
  }

  get adminTaxMappingServiceUrl() {
    return this._envSettings.getApiUrl() + this._adminTaxMappingApiRoute;
  }
  get adminServiceUrl() {
    return this._envSettings.getApiUrl() + this._adminApiRoute;
  }
  get commonServiceUrl() {
    return this._envSettings.getApiUrl() + this._commonApiRoute;
  }
  get industryServiceUrl(){
    return this._envSettings.getApiUrl() + this._industryApiRoute;
  }
  get taxExtractionServiceUrl(){
    return this._envSettings.getApiUrl() + this._taxExtractionApiRoute;
  }
  get extractionServiceUrl(){
    return this._envSettings.getApiUrl() + this._extractionApiRoute;
  }
  get evsExtractionServiceUrl(){
    return this._envSettings.getApiUrl() + this._evsExtractionApiRoute;
  }
  get capIQMappingServiceUrl(){
    return this._envSettings.getApiUrl() + this._ciqMappingApiRoute;
  }
  get capIQExtractionServiceUrl(){
    return this._envSettings.getApiUrl() + this._ciqExtractionApiRoute;
  }
  getMappingGroups(isIntangible: boolean): Observable<Array<MappingGroup>> {
    return this._http.get<Array<MappingGroup>>(this.adminEVSMappingServiceUrl + '/GetMappingGroups?isIntangible=' + isIntangible);

  }
  getIndustrySets(isIntangible: boolean): Observable<Array<IndustrySets>> {
    return this._http.get<Array<IndustrySets>>(this.adminEVSMappingServiceUrl + '/GetIndustrySets');
  }
  getReportIds(isIntangible: boolean): Observable<Array<ReportIdsForIndustrySet>> {
    return this._http.get<Array<ReportIdsForIndustrySet>>(this.adminEVSMappingServiceUrl + '/GetReportIds');
  }

  addIndustrySets(industryName: string, selectedReportIds: ReportIdsForIndustrySet[]){
    return this._http.post<any>(`${this.adminEVSMappingServiceUrl}/addNewIndustrySet/${industryName}`, selectedReportIds);
  }
  updateReportIdsToIndustrySet(updatedIds: IndustrySets[]) {
    const apipath = this.adminEVSMappingServiceUrl + '/updateReportIdsToIndustrySet';
    return this._http.post<boolean>(apipath, updatedIds);
  }
  removeIdsToIndustrySet(updatedIds: IndustrySets[]) {
    const apipath = this.adminEVSMappingServiceUrl + '/removeIdsToIndustrySet';
    return this._http.post<boolean>(apipath, updatedIds);
  }
  deleteIndustrySetName(industrySetName: string[]) {
    const apipath = this.adminEVSMappingServiceUrl + '/deleteIndustrySetName';
    return this._http.post<any>(`${this.adminEVSMappingServiceUrl}/deleteIndustrySetName`, industrySetName);
  }
  getKeywords(isIntangible: boolean): Observable<Array<KeyWord>> {
    return this._http.get<Array<KeyWord>>(this.adminEVSMappingServiceUrl + '/GetkeyWords?isIntangible=' + isIntangible);

  }
  GetIntangibleKeyWordsByGroupId(groupId: number): Observable<Array<KeyWord>> {
    return this._http.get<Array<KeyWord>>(this.adminEVSMappingServiceUrl + '/GetIntangibleKeyWordsByGroupId?groupId=' + groupId);

  }

  getDefaultMappings(): Observable<Array<DefaultIntangibleMapping>> {

    return this._http.get<Array<DefaultIntangibleMapping>>(this.adminEVSMappingServiceUrl + '/GetDefaultIntagibleMemberMappings');

  }

  updateDefaultMappings(updatedMappings: DefaultIntangibleMapping[]): Observable<Array<DefaultIntangibleMapping>> {
    const apipath = this.adminEVSMappingServiceUrl + '/UpdateDefaultIntagibleMemberMappings';
    return this._http.post<Array<DefaultIntangibleMapping>>(apipath, updatedMappings);

  }
  addMappingGroup(groupwithKeyWords: MappingGroupWithKeyWord): Observable<number> {
    const apipath = this.adminEVSMappingServiceUrl + '/AddMappingGroup';
    return this._http.post<number>(apipath, groupwithKeyWords);
  }

 
  updateIntangibleMappingGroups(groups: MappingGroup[]): Observable<Array<MappingGroup>> {
    const apipath = this.adminEVSMappingServiceUrl + '/UpdateIntangibleMappingGroups';
    return this._http.post<Array<MappingGroup>>(apipath, groups);
  }

  updatePPAMappingGroups(groups: MappingGroup[]): Observable<Array<MappingGroup>> {
    const apipath = this.adminEVSMappingServiceUrl + '/updatePPAMappingGroups';
    return this._http.post<Array<MappingGroup>>(apipath, groups);
  }

  UpdateKeyWords(keyWords: KeyWordGroupMapper[]): Observable<Array<KeyWordGroupMapper>> {
    const apipath = this.adminEVSMappingServiceUrl + '/UpdateKeyWords';
    return this._http.post<Array<KeyWordGroupMapper>>(apipath, keyWords);
  }
  // extractDataForProject(projectId: number) {
  //   return this._http.get(`${this._envSettings.getApiUrl()}/api/V1/Extraction/ExtractDataForProject/${projectId}`);
  // }
  addUser(user: User): Observable<User> {
    const apipath = this.adminServiceUrl + '/AddUser';
    return this._http.post<User>(apipath, user);
  }

  saveUserRole(editedFomrList: UserRolesElement[]): Observable<any> {
    const apipath = this.adminServiceUrl + '/SaveUserRole';
    return this._http.post<User>(apipath, editedFomrList);
  }

  getUserDetail(emailaddress: string): Observable<User> {
    const apipath = this.adminServiceUrl + '/GetUserByEmail';   
    return this._http.post<User>(apipath,{emailAddress:emailaddress});
    // return this._http.get<User>(`${this.adminServiceUrl}/GetUserByEmail/${emailaddress}`);
  }
  provisionUser(user: ProvisionUser): Observable<ProvisionUser> {
    const apipath = this.adminServiceUrl + '/user/provision';
    return this._http.post<ProvisionUser>(apipath, user);
  }

  provisionAllUsers(): Observable<string>{
    const apipath = this.adminServiceUrl + '/user/provisionBulk';
    return this._http.get<string>(apipath);
  }

  getAllUsers() : Observable<Array<User>> {
    return this._http.get<Array<User>>(this.commonServiceUrl + '/Users');
  }
  
  addUsersToRole(users: string[]): Observable<Array<Role>> {
    var data = { emailAddresses: users, roleName: 'Admin' }
    const apipath = this.adminServiceUrl + '/AddUsersToRole';
    return this._http.post<Array<Role>>(apipath, JSON.stringify(data));
  }
  addUsersToEvsRole(users: string[]): Observable<Array<Role>> {
    var data = { emailAddresses: users, roleName: 'Evs' }
    const apipath = this.adminServiceUrl + '/AddUsersToEvsRole';
    return this._http.post<Array<Role>>(apipath, JSON.stringify(data));
  }
  
  getAdminUsers(): Observable<Array<UserWithRole>> {
    return this._http.get<Array<UserWithRole>>(this.adminServiceUrl + '/GetAdminUsers');
  }
  getEvsUsers(): Observable<Array<User>> {
    return this._http.get<Array<User>>(this.adminServiceUrl + '/GetEvsUsers');
  }

  getAllUsersDetails(pageNo:number,pageSize:number,sortColumn:string,sortDirection:string): Observable<any> {
    return this._http.post<any>(`${this.adminServiceUrl}/GetAllUsers/${pageNo}/${pageSize}/${sortColumn}`, JSON.stringify(sortDirection));
  }

  getAllFilterUsersDetails(pageNo:number,pageSize:number,columnName:string,sortDirection:string,filterOption:UserRolesElement): Observable<any> {
    var data = { pageNo: pageNo, pageSize: pageSize,sortColumn:columnName,sortDirection:sortDirection,userId:filterOption.userId,userName:filterOption.userName,email:filterOption.email,title:filterOption.title,companyCountryCode:filterOption.companyCountryCode,isAdminUser:filterOption.isAdminUser,isEvsUser:filterOption.isEvsUser,isReviewerUser:filterOption.isReviewerUser,modifiedBy:filterOption.modifiedBy,expirationStatus:filterOption.expirationStatus}
    const apipath = this.adminServiceUrl + '/FilterAllUser';
    return this._http.post<any>(apipath,JSON.stringify(data));
  }

  updateUsersDetails(userId: number, isDeActivated: boolean){
    return this._http.post<any>(`${this.adminServiceUrl}/updateUserDetails/${userId}`, isDeActivated);
  }

  deleteUserDetails(userId: number[]){
    return this._http.post<any>(`${this.adminServiceUrl}/deleteUserDetails`, userId);
  }
  searchAllUserInfo(searchTerm: string): any {
    return this._http.get<Array<User>>(this.adminServiceUrl + '/SearchAllUser/' + searchTerm);
  }

  downloadAuditTrail(): Observable<Blob> {
    return this._http.get(this.adminServiceUrl + '/DownloadAuditTrail', { responseType: 'blob' });
  }
  downloadValidationTrail(): Observable<Blob> {
    return this._http.get(this.adminServiceUrl + '/DownloadValidationStatus', { responseType: 'blob' });
  }
  downloadUsageReport(): Observable<Blob> {
    return this._http.get(this.adminServiceUrl + '/GetLeadershipDashboardData', { responseType: 'blob' });
  }
  downloadUserActivityLogs(): Observable<Blob> {
    return this._http.get(this.adminServiceUrl + '/DownloadUserActivityLogs', { responseType: 'blob' });
  }
  getApplicationInfo(): Observable<Array<ApplicationInfo>> {
    return this._http.get<Array<ApplicationInfo>>(this.adminServiceUrl + '/GetApplicationInfos');
  }

  getApplicationInfoByTag(tag:string): Observable<ApplicationInfo> {
    return this._http.get<ApplicationInfo>(this.adminServiceUrl + '/GetApplicationInfoByTag/'+tag);
  }
  UpdateApplicationInfos(updatedInfos: ApplicationInfo[]): Observable<ApplicationInfo[]> {
    const apipath = this.adminServiceUrl + '/UpdateApplicationInfos';
    return this._http.post<ApplicationInfo[]>(apipath, updatedInfos);
  }
  //#region  Tax Use Case

  getBalanceSheetCategories(): Observable<Array<BalanceSheetCategory>> {
    return this._http.get<Array<BalanceSheetCategory>>(this.adminTaxMappingServiceUrl + '/GetBalanceSheetCategories');

  }

  getIncomeStatementCategories(): Observable<Array<IncomeStatementCategory>> {
    return this._http.get<Array<IncomeStatementCategory>>(this.adminTaxMappingServiceUrl + '/GetIncomeStatementCategories');

  }
  getIncomeTaxDisclosureCategories(): Observable<Array<IncomeTaxDisclosureCategory>> {
    return this._http.get<Array<IncomeTaxDisclosureCategory>>(this.adminTaxMappingServiceUrl + '/GetIncomeTaxDisclosureCategories');

  }

  addBalanceSheetCategory(category: BalanceSheetCategory): Observable<BalanceSheetCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/AddBalanceSheetCategory';
    return this._http.post<BalanceSheetCategory>(apipath, category);
  }


  addIncomeStatementCategory(category: IncomeStatementCategory): Observable<IncomeStatementCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/AddIncomeStatementCategory';
    return this._http.post<IncomeStatementCategory>(apipath, category);
  }
  addIncomeTaxDisclosureCategory(category: IncomeTaxDisclosureCategory): Observable<IncomeTaxDisclosureCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/AddIncomeTaxDisclosureCategory';
    return this._http.post<IncomeTaxDisclosureCategory>(apipath, category);
  }

  updateBalanceSheetCategories(categories: BalanceSheetCategory[]): Observable<BalanceSheetCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateBalanceSheetCategory';
    return this._http.post<BalanceSheetCategory>(apipath, categories);
  }

  updateIncomeStatementCategories(categories: IncomeStatementCategory[]): Observable<IncomeStatementCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateIncomeStatementCategory';
    return this._http.post<IncomeStatementCategory>(apipath, categories);
  }

  updateIncomeTaxDisclosureCategories(categories: IncomeTaxDisclosureCategory[]): Observable<IncomeTaxDisclosureCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateIncomeTaxDisclosureCategory';
    return this._http.post<IncomeTaxDisclosureCategory>(apipath, categories);
  }

  getBalanceSheetDefaultMappings(): Observable<Array<DefaultMappingGroup>> {
    return this._http.get<Array<DefaultMappingGroup>>(this.adminTaxMappingServiceUrl + '/GetBalanceSheetDefaultMappings');

  }

  getIncomeStatementDefaultMappings(): Observable<Array<DefaultMappingGroup>> {
    return this._http.get<Array<DefaultMappingGroup>>(this.adminTaxMappingServiceUrl + '/GetIncomeStatementDefaultMappings');

  }

  getIncomeTaxDisclosureDefaultMappings(): Observable<Array<DefaultMappingGroup>> {
    return this._http.get<Array<DefaultMappingGroup>>(this.adminTaxMappingServiceUrl + '/GetIncomeTaxDisclosureDefaultMappings');

  }

  updateBalanceSheetDefaultMappings(mappings: DefaultMappingGroup[]): Observable<DefaultMappingGroup> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateBalanceSheetDefaultMappings';
    return this._http.post<DefaultMappingGroup>(apipath, mappings);
  }

  updateIncomeStatementDefaultMappings(mappings: DefaultMappingGroup[]): Observable<DefaultMappingGroup> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateIncomeStatementDefaultMappings';
    return this._http.post<DefaultMappingGroup>(apipath, mappings);
  }

  updateIncomeTaxDisclosureDefaultMappings(mappings: DefaultMappingGroup[]): Observable<DefaultMappingGroup> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateIncomeTaxDisclosureDefaultMappings';
    return this._http.post<DefaultMappingGroup>(apipath, mappings);
  }

  GetTaxTagCategorization(): Observable<Array<TaxTagCategory>> {
    return this._http.get<Array<TaxTagCategory>>(this.adminTaxMappingServiceUrl + '/GetTaxTagCategorization');

  }

  addTaxTagCategorization(category: TaxTagCategory): Observable<TaxTagCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/AddTaxTagCategorization';
    return this._http.post<TaxTagCategory>(apipath, category);
  }

  updateTaxTagCategorization(updatedCategories: TaxTagCategory[]): Observable<TaxTagCategory> {
    const apipath = this.adminTaxMappingServiceUrl + '/UpdateTaxTagCategorization';
    return this._http.post<TaxTagCategory>(apipath, updatedCategories);
  }

  
  //#endregion
  getCompanies(): Observable<Array<Company>> {
    return this._http.get<Array<Company>>(this.industryServiceUrl + '/GetCompanies');
  }
  getAccessionNumbers(): Observable<Array<string>> {
    return this._http.get<Array<string>>(this.adminServiceUrl + '/GetAccessionNumbers');
  }

  extractMissingFilingStages(bulkCount:number,extractionQuery: ExtractionQuery):Observable<any>{
    const apipath = this.extractionServiceUrl + '/ExtractMissingFilingStages?bulkCount='+bulkCount;
    return this._http.post<any>(apipath, extractionQuery);
  }
  extractMissingFilingStages8k(bulkCount:number,extractionQuery: ExtractionQuery):Observable<any>{
    const apipath = this.extractionServiceUrl + '/ExtractMissingFilingStages8k?bulkCount='+bulkCount;
    return this._http.post<any>(apipath, extractionQuery);
  }
  extractMissingFeedDataWithoutValidMetadata(bulkCount:number,extractionQuery: ExtractionQuery):Observable<any>{
    const apipath = this.extractionServiceUrl + '/ExtractMissingFeedDataWithoutValidMetadata?bulkCount='+bulkCount;
    return this._http.post<any>(apipath, extractionQuery);
  }
  
  getMissingFilingStagesCount(extractionQuery: ExtractionQuery):Observable<number>{
    
    const apipath = this.extractionServiceUrl + '/GetMissingFilingStagesCount';
    return this._http.post<number>(apipath, extractionQuery);
  }
  getMissingFilingStagesCount8k(extractionQuery: ExtractionQuery):Observable<number>{
    
    const apipath = this.extractionServiceUrl + '/GetMissingFilingStagesCount8k';
    return this._http.post<number>(apipath, extractionQuery);
  }
  getFailedInvalidMetadataFilingsCount(extractionQuery: ExtractionQuery):Observable<number>{
   
    const apipath = this.extractionServiceUrl + '/GetInvalidaMetadataFilingCount';
    return this._http.post<number>(apipath, extractionQuery);
  }
  getNonInitiatedFeedDataCount(): Observable<number> {
    return this._http.get<number>(this.adminServiceUrl + '/GetNonInitiatedFeedDataCount');
  }
  getNonInitiatedFeedDataCount8k(): Observable<number> {
    return this._http.get<number>(this.adminServiceUrl + '/GetNonInitiatedFeedDataCount8k');
  }
  extractMissingFeedData(bulkCount:number): Observable<any> {
    return this._http.post<any>(`${this.extractionServiceUrl}/ExtractMissingFeedData`, bulkCount);
  }
  extractMissingFeedData8k(bulkCount:number): Observable<any> {
    return this._http.post<any>(`${this.extractionServiceUrl}/ExtractMissingFeedData8k`, bulkCount);
  }
  addAccessionNumbers(accessionNos:string[]): Observable<Array<string>> {
    
    const apipath = this.adminServiceUrl + '/AddAccessionNumbers';
    return this._http.post<Array<string>>(apipath, accessionNos);
  }
  add8kExtractionDetails(urls:string[]): Observable<boolean> {
    
    const apipath = this.adminServiceUrl + '/Add8KExtractionDetails';
    return this._http.post<boolean>(apipath, urls);
  }
  getExtractorDetails(): Observable<Array<ExtractionDetail>> {
    return this._http.get<Array<ExtractionDetail>>(this.adminServiceUrl + '/GetExtractionDetail');
  }
  

  GetExtractorStatusDetails(extractionQuery: ExtractionQuery,includeNonExtracted:boolean,includeFailedStage:boolean): Observable<Array<ExtractionDetail>> {
   const apipath = this.adminServiceUrl + '/GetExtractorStatusDetails?includeNonExtracted='+includeNonExtracted+'&includeFailedStage='+includeFailedStage;
    return this._http.post<Array<ExtractionDetail>>(apipath, extractionQuery);
  }

  extractMetadataInfo(accessionNumber:string): Observable<CompanyFiling> {
    return this._http.post<CompanyFiling>(`${this.extractionServiceUrl}/GetFilingMetaData`, JSON.stringify(accessionNumber));
  }
  extractEightkMetadataInfo(accessionNumber: string, cik: number): Observable<any> {
    return this._http.post<any>(`${this.extractionServiceUrl}/GetEightkMetaData/${accessionNumber}`, cik);
  }
  extractEightkHTMLFile(accessionNumber: string, cik: number): Observable<any> {
    return this._http.post<any>(`${this.extractionServiceUrl}/ExtractFilingHtmlFileEightk/${accessionNumber}`, cik);
  }
  extractEightkExhibitsInfo(accessionNumber: string, cik: number): Observable<any> {
    return this._http.post<any>(`${this.extractionServiceUrl}/ExtractEightkExhibitsInfo/${accessionNumber}`, cik);
  }
  extractEightkExhibits(accessionNumber: string, cik: number): Observable<any> {
    return this._http.post<any>(`${this.extractionServiceUrl}/ExtractEightkExhibits/${accessionNumber}`, cik);
  }
  extractHTMLLink(reportId:string): Observable<any> {
    var HTTPOptions = {
      headers: new HttpHeaders({
         'Accept':'text/html'
      }),
      responseType: 'text'
   }
    return this._http.post(`${this.extractionServiceUrl}/UpdateFilingFormUrl`, reportId,{responseType: 'text'});
  }

  extractHTMLFile(reportId:string): Observable<any> {
    return this._http.post(`${this.extractionServiceUrl}/ExtractFilingHtmlFile`, reportId);
  }

  extractJSONFile(reportId:string): Observable<any> {
    return this._http.post(`${this.extractionServiceUrl}/GetFilingJson`, reportId);
  }

  extractEvsInfo(reportId:string): Observable<any> {
    return this._http.post(`${this.evsExtractionServiceUrl}/ParseAcquisitionInfo`, reportId);
  }

  extractTaxInfo(reportId:string): Observable<any> {
    return this._http.post(`${this.taxExtractionServiceUrl}/ExtractFinancialStatements`, reportId);
  }

  extractNotes(reportId:string): Observable<any> {
    return this._http.post(`${this.extractionServiceUrl}/ExtractFootNotes`, reportId);
  }

  extractStatements(reportId:string): Observable<any> {
    return this._http.post(`${this.extractionServiceUrl}/ExtractStatements`, reportId);
  }
  
  getNetworkRoleDescriptionBySection(section:string): Observable<Array<NetworkRoleDescription>> {
    return this._http.get<Array<NetworkRoleDescription>>(this.adminServiceUrl + '/GetNetworkRoleDescriptionBySection/'+section);
  }
  addNetworkRoleDescription(networkRole:NetworkRoleDescription): Observable<NetworkRoleDescription> {
    
    const apipath = this.adminServiceUrl + '/AddNetworkRoleDescription';
    return this._http.post<NetworkRoleDescription>(apipath, networkRole);
  }
  addCIQTransactionIds(transactionids : string[]): Observable<boolean> {
    
    const apipath = this.adminServiceUrl + '/AddCIQTransactionIDs';
    return this._http.post<boolean>(apipath, transactionids);
  }
  uploadCIQTransactionIds(transactionids: string[]): Observable<boolean> {
    const apipath = this.adminServiceUrl + '/UploadCIQTransactionIDs';
    return this._http.post<boolean>(apipath, transactionids);
  }
  extractCIQTransactionIds(bulkCount:number): Observable<any> {
    return this._http.post<any>(`${this.capIQExtractionServiceUrl}/ExtractCIQFeedData`, bulkCount);
  }
  UpdateIndustries() : Observable<any> {
    return this._http.get<any>(this.capIQExtractionServiceUrl + '/UpdateIndustries');
  }
  getNonInitiatedFeedDataCountCIQ(): Observable<number> {
    return this._http.get<number>(this.adminServiceUrl + '/GetNonInitiatedFeedDataCountCIQ');
  }
  mapCIQTransactions(bulkCount : number) : Observable<any> {
    return this._http.post(`${this.capIQMappingServiceUrl}/MapCIQToFilingsinBatch`, bulkCount);
  }
  mapSingleTransaction(transactionId: string) : Observable<any> {
    return this._http.post(`${this.capIQMappingServiceUrl}/MapSingleTransaction`, JSON.stringify(transactionId));
  }
  mapAllCIQTransactions() : Observable<any> {
    return this._http.get(this.capIQMappingServiceUrl + '/MapAllCIQTransactions');
  }
  getAllCIQs(): Observable<Array<CIQInfo>> {
    return this._http.get<Array<CIQInfo>>(this.capIQMappingServiceUrl + '/GetAllCIQs');
  }
  getAllCIQExtractionDetails(): Observable<Array<CIQExtraction>> {
    return this._http.get<Array<CIQExtraction>>(this.capIQExtractionServiceUrl + '/GetAllCIQExtractionDetails');
  }
  extractSingleCIQTransaction(ciqTransactionId: string): Observable<CIQExtraction> {
    return this._http.post<CIQExtraction>(`${this.capIQExtractionServiceUrl}/ExtractSingleCIQTransaction`, JSON.stringify(ciqTransactionId));
  }
  getCompanyAcquisitionsByCIQInfoID(ciqInfoId: number): Observable<Array<CompanyAcquisitions>> {

    return this._http.get<Array<CompanyAcquisitions>>(this.capIQMappingServiceUrl +
      `/GetCompanyAcquisitionsByCIQInfoID?ciqInfoId=${ciqInfoId}`);

  }
  updateCIQMapping(ciqMapIds : CIQMapID[]): Observable<boolean> {
    
    const apipath = this.capIQMappingServiceUrl + '/UpdateCIQMapping';
    return this._http.post<boolean>(apipath, ciqMapIds);
  }
  getCIQMappingCount(): Observable<CIQMapCount> {
    return this._http.get<CIQMapCount>(this.capIQMappingServiceUrl + '/GetCIQMappingCount');
  }

  getNonInitiatedFeedDataCIQ(): Observable<Array<CIQExtraction>> {
    return this._http.get<Array<CIQExtraction>>(this.adminServiceUrl + '/GetNonInitiatedFeedDataCIQ');
  }

  extractAllFailedCIQTransaction(): Observable<Array<CIQExtraction>> {
    return this._http.get<Array<CIQExtraction>>(this.capIQExtractionServiceUrl + '/ExtractAllFailedCIQTransaction');
  }
}
